import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';

import {
  DocumentUploadPage,
  DocumentUploadData,
} from '@karehero/llama/pwa/pages/DocumentUploadPage/DocumentUploadPage';

import { isSystemTag } from '@karehero/models';
import { useGoBack } from 'hooks/useGoBack';

import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { newToast } from 'store/slices/uiSlice';
import {
  useListOrganiseDocumentTagsQuery,
  useRequestOrganiseDocumentUploadURLMutation,
  useConfirmOrganiseDocumentUploadMutation,
} from 'store/api/organiseDocument';

export const DocumentUpload = () => {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const careCircle = useSelector(getCurrentCareCircle);
  const careCircleId = careCircle?.id ?? '';

  const [isUploading, setIsUploading] = useState(false);

  const { data: allTags = [] } = useListOrganiseDocumentTagsQuery(
    careCircleId,
    {
      skip: !careCircleId,
    },
  );
  const tags = allTags.filter((tag) => !isSystemTag(tag.id));

  const [requestUploadURL] = useRequestOrganiseDocumentUploadURLMutation();
  const [confirmUpload] = useConfirmOrganiseDocumentUploadMutation();

  const handleUpload = useCallback(
    async (data: DocumentUploadData) => {
      if (!careCircleId) return;

      setIsUploading(true);

      try {
        const response = await requestUploadURL({
          careCircleId,
          body: {
            title: data.title,
            description: data.description,
            filename: data.file.name,
            contentType: data.file.type,
            sizeBytes: data.file.size,
            tags: data.tags.length > 0 ? data.tags : undefined,
          },
        }).unwrap();

        const { documentId, uploadUrl } = response;

        if (!uploadUrl) {
          throw new Error('No upload URL in response');
        }

        // Use POST for JSON API endpoints (emulator), PUT for signed URLs (production)
        const isJSONUploadEndpoint = uploadUrl.includes('/upload/storage/');

        const uploadResponse = await fetch(uploadUrl, {
          method: isJSONUploadEndpoint ? 'POST' : 'PUT',
          body: data.file,
          headers: {
            'Content-Type': data.file.type,
          },
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          throw new Error(`Upload to storage failed: ${errorText}`);
        }

        await confirmUpload({
          careCircleId,
          body: { documentId },
        }).unwrap();

        dispatch(
          newToast({
            title: 'Document uploaded',
            description: 'Your document has been uploaded successfully.',
          }),
        );

        goBack();
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Upload failed',
            description:
              error instanceof Error
                ? error.message
                : 'An error occurred while uploading the document.',
          }),
        );
      } finally {
        setIsUploading(false);
      }
    },
    [careCircleId, requestUploadURL, confirmUpload, goBack, dispatch],
  );

  const isNative = Capacitor.isNativePlatform();

  return (
    <DocumentUploadPage
      availableTags={tags}
      isUploading={isUploading}
      isNative={isNative}
      onBack={goBack}
      onUpload={handleUpload}
    />
  );
};

export default DocumentUpload;
